import { Box, Button, Drawer, Flex, Loader, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { LuDownload } from "react-icons/lu";
import { https } from "../../http";
import { showError } from "../../utils";
import useStyles from "./style";

// Download filename, without extension: the note type name followed by today's
// date. Kept in step with the Content-Disposition the API sets on /note/:id/pdf.
export const singleNotePdfFileName = (noteTypeName, now = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${now.getFullYear()}`;
  const name = String(noteTypeName || "Note")
    // Characters Windows/macOS reject in a filename, then collapse the
    // whitespace they leave behind. Hyphens are legal and stay put, so a type
    // like "Follow-up Note" keeps its name.
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    // A leading dot would make it a hidden file on macOS/Linux.
    .replace(/^\.+/, "");
  return `${name || "Note"} ${stamp}`;
};

// Row-level "Download PDF" (kebab menu, /home/note table) — shows a
// pixel-matched replica of the notes-redesign demo's single-note "PDF
// Preview" (letterhead, note-type title, 2-col meta grid, adaptive content
// sections, staff signature) before the real PDF download. One uniform
// template for every note type, including Incident Report — distinct from
// both the multi-note report's per-entry layout and the dedicated Incident
// Report batch-PDF layout.
const SingleNotePdfModal = ({ noteId, opened, onClose }) => {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);
  // Anchor lives INSIDE the drawer so its programmatic .click() targets an
  // element within the drawer — clicking a detached/out-of-drawer anchor was
  // read as an outside interaction and auto-closed the drawer after download.
  const downloadLinkRef = useRef(null);
  // The download click triggers a spurious Drawer onClose (outside-interaction /
  // blur from the browser's download). Suppress any close fired in the short
  // window around the download so the drawer stays open; normal Close/X still work.
  const suppressCloseRef = useRef(false);

  const handleClose = () => {
    if (suppressCloseRef.current) return;
    onClose?.();
  };

  // Flat [{label, text, heading?}] blocks — the built-in note types' whole body,
  // and the care-plan blocks that follow a studio template's cards. Mirrors
  // drawFlatSections in the PDF generator so preview and file stay in step.
  const renderFlatSections = (list) =>
    (list || []).map((sec, i) =>
      sec.heading ? (
        <div key={i} className={classes.sectionHeading}>{sec.label}</div>
      ) : (
        <Box key={i}>
          <div className={classes.sectionLabel}>{sec.label}</div>
          <div className={classes.sectionBody}>{sec.text}</div>
        </Box>
      )
    );

  useEffect(() => {
    if (!opened || !noteId) return;
    setData(null);
    setLoading(true);
    https()
      .get(`/note/${noteId}/previewData`)
      .then((res) => setData(res.data))
      .catch((error) => {
        console.error("Note PDF preview error:", error);
        showError("Failed to load note preview.");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [opened, noteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await https().get(`/note/${noteId}/pdf`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = downloadLinkRef.current;
      link.href = url;
      // "<Note type> MM-DD-YYYY.pdf", e.g. "Progress Note 08-04-2026.pdf". The
      // `download` attribute wins over the response's Content-Disposition for a
      // blob URL, but the API sets the same name for direct callers. Characters
      // Windows/macOS reject in a filename are stripped from the type name,
      // which is free user text from the Note Type Studio.
      link.download = `${singleNotePdfFileName(data?.noteTypeName)}.pdf`;
      // Block the spurious close the click/download provokes, then re-enable
      // closing shortly after so the user can still dismiss the drawer.
      suppressCloseRef.current = true;
      link.click();
      // Revoke after the click has been handled so the download isn't cancelled.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setTimeout(() => {
        suppressCloseRef.current = false;
      }, 800);
    } catch (error) {
      console.error("Note PDF download error:", error);
      showError("Failed to download PDF.");
      suppressCloseRef.current = false;
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="45%"
      zIndex={1200}
      title={
        <Text fz={24} fw={700} ff="Inter" color="var(--app-text)">
          PDF Preview
        </Text>
      }
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
      shadow="md"
    >
      {/* Hidden download anchor — kept inside the drawer so its click isn't
          treated as an outside interaction that would close the drawer. */}
      <a ref={downloadLinkRef} style={{ display: "none" }} aria-hidden="true">
        download
      </a>
      <Flex direction="column" style={{ height: "100%" }}>
        <Box style={{ flex: 1,  overflowY: "auto" }} py={14} px={24}>
          {loading && (
            <Flex justify="center" align="center" mih={"78vh"}>
              <Loader color="var(--app-primary)" />
            </Flex>
          )}
          {!loading && data && (
            <div className={classes.page}>
              <div className={classes.letterhead}>
                <div className={classes.brand}>
                  {/* Logo / initials only when "Include agency letterhead" is on. */}
                  {data.includeLetterhead &&
                    (data.facilityLogo ? (
                      <img src={data.facilityLogo} alt="" className={classes.logoImg} />
                    ) : (
                      <div className={classes.logo}>{data.facilityInitials}</div>
                    ))}
                  <div className={classes.org}>{data.facilityName}</div>
                </div>
                <div className={classes.lhRight}>
                  Note #{data.noteNumber}
                  <br />
                  {data.status}
                </div>
              </div>
              <h1 className={classes.title}>{data.noteTypeName}</h1>
              {/* Studio template notes render Client/Staff/Facility/Date/Time
                  inline in their designed sections, so the generic meta grid is
                  skipped to mirror the template exactly. */}
              {!(data.templateSections && data.templateSections.length) && (
                <div className={classes.meta}>
                  <div>
                    <div className={classes.metaK}>Client</div>
                    <div className={classes.metaV}>{data.clientNames.join(", ") || "None"}</div>
                  </div>
                  <div>
                    <div className={classes.metaK}>Facility</div>
                    <div className={classes.metaV}>{data.facilityName || "None"}</div>
                  </div>
                  <div>
                    <div className={classes.metaK}>Staff</div>
                    <div className={classes.metaV}>{data.staffNames.join(", ") || "None"}</div>
                  </div>
                  <div>
                    <div className={classes.metaK}>Date &amp; Time</div>
                    <div className={classes.metaV}>
                      {data.date}
                      {data.time ? ` · ${data.time}` : ""}
                    </div>
                  </div>
                </div>
              )}
              {data.templateSections && data.templateSections.length ? (
                <>
                  {/* Studio-built note types: reproduce the designed grid layout —
                      a bordered section card per section, fields laid out in a
                      12-col grid honoring each field's width, with help captions. */}
                  {data.templateSections.map((sec, si) => (
                    <div key={si} className={classes.tplCard}>
                      <div className={classes.tplCardHead}>{sec.title}</div>
                      <div className={classes.tplGrid}>
                        {sec.fields.map((fld, fi) => (
                          <div key={fi} style={{ gridColumn: `span ${fld.span || 12}` }}>
                            <div className={classes.tplFieldLabel}>{fld.label}</div>
                            <div className={classes.tplFieldValue}>{fld.value || "—"}</div>
                            {fld.help ? <div className={classes.tplFieldHelp}>{fld.help}</div> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* Care-plan documentation (Support Plan Outcomes / Essential
                      Support) captured on the note via its "Use care plan?" field.
                      Not part of the designed template, so — exactly like the PDF
                      — it follows the template cards as flat labelled blocks. The
                      backend only populates this for template notes; every other
                      note type carries it inside `sections` below. */}
                  {renderFlatSections(data.carePlanSections)}
                </>
              ) : (
                renderFlatSections(data.sections)
              )}
              <div className={classes.signWrap}>
                <div className={classes.signLbl}>
                  Staff Signature{data.staffSignatures.length > 1 ? "s" : ""}
                </div>
                {data.staffSignatures.length > 0 ? (
                  data.staffSignatures.map(({ name, signatureImage, signatureText }) => (
                    <div key={name}>
                      {signatureImage ? (
                        <img src={signatureImage} alt="Signature" className={classes.sigImg} />
                      ) : signatureText ? (
                        <div className={classes.sig}>{signatureText}</div>
                      ) : (
                        <div className={classes.sig}>{name}</div>
                      )}
                      <div className={classes.sigSm}>
                        Signed by {name} · {data.date} {data.time}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={classes.sigSm}>Not yet signed</div>
                )}
              </div>
              {data.supervisorSignature && (
                <div className={classes.signWrap}>
                  <div className={classes.signLbl}>Supervisor Signature</div>
                  {data.supervisorSignature.signatureImage ? (
                    <img
                      src={data.supervisorSignature.signatureImage}
                      alt="Supervisor Signature"
                      className={classes.sigImg}
                    />
                  ) : (
                    <div className={classes.sig}>
                      {data.supervisorSignature.signatureText ||
                        data.supervisorSignature.name}
                    </div>
                  )}
                  <div className={classes.sigSm}>
                    Approved by {data.supervisorSignature.name}
                    {data.supervisorSignature.signedAt
                      ? ` · ${data.supervisorSignature.signedAt}`
                      : ""}
                  </div>
                </div>
              )}
              <div className={classes.footer}>
                <span>{data.facilityName} · Confidential clinical record</span>
                <span>Page 1 of 1</span>
              </div>
            </div>
          )}
        </Box>
        <Flex gap={20} align="center" justify="space-between" className={classes.footerBar}>
          <Button className={classes.cancelBtn} onClick={onClose} type="button">
            Close
          </Button>
          <Button
            className={classes.downloadBtn}
            onClick={handleDownload}
            loading={downloading}
            disabled={loading || !data}
            type="button"
          >
            <LuDownload size={15} style={{ marginRight: 6 }} />
            Download PDF
          </Button>
        </Flex>
      </Flex>
    </Drawer>
  );
};

export default SingleNotePdfModal;
